import type { GameSymbol, SymbolStyle } from '../game/symbols'
import { generatedStyle } from '../game/symbols'
import type { Theme, ThemeSymbolStyle } from './schema'

/**
 * Theme resolution: manifest + game symbols → everything the renderer needs.
 *
 * Resolution is per-field, not per-theme. A manifest that sets only `fill`
 * for one symbol keeps the generated label, size and colour for every other
 * one. That is what lets a skin start as three colours and grow into full
 * art without a rewrite — and what keeps a game with NO theme rendering
 * exactly as it did before themes existed.
 *
 * No Pixi here on purpose: the lobby resolves themes to draw previews, and
 * it must not pay for a WebGL context to do it. Textures are attached later
 * by `loadThemeAssets` (see `load.ts`).
 */

/**
 * A particle source anchored to the backdrop art. All geometry is in
 * fractions of the BACKDROP IMAGE, not of the viewport, so it stays on the
 * painted waterfall whatever `background-size: cover` crops away.
 */
export interface StageEmitter {
  kind: 'waterfall' | 'mist' | 'cloud' | 'glow'
  x: number
  y: number
  width: number
  height: number
  /** Signed sideways drift over the fall, as a fraction of image width. */
  curve: number
  /** Cloud travel corridor as a fraction of image width. 1 = edge to edge. */
  span: number
  color: number
  /** Particles per second, at the art's own scale. */
  rate: number
  alpha: number
  /** `glow` only: seconds for one full breath of the aura. */
  period: number
}

export interface ResolvedTheme {
  id: string
  name: string
  header: {
    /** Absolute URL, or null when the live text title should render. */
    logoImage: string | null
  }
  /** Win-celebration lettering by tier slug. Null where the theme ships none. */
  winTitles: Record<string, string | null>
  /** Gems raining behind the celebration. Null where the theme ships none. */
  winCrystals: { red: string | null; blue: string | null }
  /** Plaque behind the celebration's confirm button, or null. */
  winButton: string | null
  /** Styles by symbol code. Every code of the game is present. */
  symbols: Record<string, SymbolStyle>
  bomb: SymbolStyle
  board: {
    background: number | null
    /** Absolute URL, or null. */
    backgroundImage: string | null
    /** Absolute URL, or null. */
    frameImage: string | null
    /** Where the grid sits inside the frame art, per side. null ⇒ cover. */
    frameInset: { left: number; right: number; top: number; bottom: number } | null
    /** Visible opening around the grid; may be larger to create a liner gap. */
    frameOpeningInset: { left: number; right: number; top: number; bottom: number } | null
    tileRadius: number
    gap: number | null
    /** Explicit cell size on screen, or null for the mechanic's default. */
    cellSize: number | null
    /** Quiet margin between the grid and the canvas edge, board px. */
    gridPadding: number
    symbolPadding: number
    symbolFit: 'contain' | 'stretch'
  }
  /** Page-level dressing. Applied as CSS, not drawn by the renderer. */
  stage: {
    background: number | null
    /** Absolute URL, or null. */
    backgroundImage: string | null
    /** Absolute URL, or null. Decorative character next to the board. */
    mascotImage: string | null
    /** Absolute URL, or null. Scenery on the other side of the board. */
    decorImage: string | null
    /** Absolute URL, or null. Floating creature drawn on both sides. */
    ghostImage: string | null
    panel: boolean
    /** Fire-coloured (or any) restyle of the stock panel. null = stock. */
    panelFrame: { border: number[]; background: number[] | null } | null
    /** Cloud sprite URLs for `cloud` emitters. Empty ⇒ procedural clouds. */
    cloudImages: string[]
    /** Which big-win celebration the overlay plays. */
    celebration: 'flames' | 'sunburst'
    /** Particle sources pinned to the backdrop art. Empty for most themes. */
    emitters: StageEmitter[]
  }
  /**
   * Sound. `music` is an absolute URL or null; `sfx` maps event names to
   * absolute URLs and is empty when the theme ships no effects.
   */
  audio: {
    music: string | null
    /** Livelier loop crossfaded in during free spins, or null. */
    musicFree: string | null
    musicVolume: number
    sfx: Record<string, string>
    sfxVolume: number
  }
  font: { family: string; weight: string; letterSpacing: number }
  banners: Record<BannerKind, ResolvedBanner>
  timings: { tumble: TumbleTiming; ways: WaysTiming; lines: LinesTiming }
  /** Absolute atlas URL, or null when the theme ships no art. */
  atlas: { url: string; backgroundFrame: string | null } | null
  /** Loaded frames by symbol code. Empty until `loadThemeAssets` runs. */
  textures: Map<string, unknown>
}

export type BannerKind = 'free' | 'win' | 'bomb'

/**
 * A banner's look. `image` turns the drawn rectangle into a stretchable
 * plaque: `cap` pixels at each end stay fixed and everything between them
 * repeats, so one file serves "×2" and "FREE SPINS: 1,250,000" alike.
 */
export type ResolvedBanner = {
  fill: number
  text: number
  image: string | null
  cap: number
  fieldTop: number
  fieldBottom: number
  shadow: number | null
}
export type TumbleTiming = typeof DEFAULT_TUMBLE_TIMING
export type WaysTiming = typeof DEFAULT_WAYS_TIMING
export type LinesTiming = typeof DEFAULT_LINES_TIMING

/**
 * Renderer defaults, in ms. These are the values the placeholder client
 * shipped with — a theme overrides what it cares about, inside the bands
 * declared in `schema.ts`.
 */
export const DEFAULT_TUMBLE_TIMING = {
  /** Small compression before the old board starts moving. */
  spinAnticipationMs: 0,
  /** Old cells leaving the board before the new result enters. */
  spinExitMs: 0,
  /** Start delay between neighbouring columns during the exit. */
  spinExitStaggerMs: 0,
  /** Start delay between rows during the exit. */
  spinExitRowStaggerMs: 0,
  /** Readable empty beat between the outgoing and incoming boards. */
  emptyHoldMs: 0,
  /** Cell falling in from above when the board fills. */
  dropMs: 380,
  /** Start delay between neighbouring columns. */
  dropStaggerMs: 45,
  /** Delay between rows in a column (bottom ones land first). */
  dropRowStaggerMs: 30,
  /** Impact compression after a falling cell reaches its target. */
  landingMs: 0,
  /** Replacement cells move faster than a full-board spin. */
  cascadeDropMs: 380,
  /** Cascades collapse locally rather than replaying the full spin wave. */
  cascadeColumnStaggerMs: 0,
  /** Small ordering cue between replacement rows in one column. */
  cascadeRowStaggerMs: 30,
  /** Optional impact reserved for replacement cells. */
  cascadeLandingMs: 0,
  /** Winning symbols pulse before they blow up. */
  highlightMs: 520,
  /** The explosion itself. */
  explodeMs: 220,
  /** Survivors settling down. */
  settleMs: 300,
  /** Bomb total shown at the end of a free-spin chain. */
  bombMs: 900,
  /** A multiplier's own arrival: it spins into its tilt and pops. */
  bombEntranceMs: 420,
  /** Scatter pulse on trigger / retrigger. */
  scatterMs: 700,
  /** Pause between spins inside one round. */
  betweenSpinsMs: 500,
  /** Pause before free spins start. */
  freeSpinsIntroMs: 1100,
}

export const DEFAULT_WAYS_TIMING = {
  /** Start delay per reel — makes the cascade read. */
  reelStartStagger: 90,
  /** Minimum spin time before the first reel may stop. */
  minSpinDuration: 700,
  /** Stop delay per reel. */
  reelStopStagger: 160,
  /** Pause between free spins. */
  betweenFreeSpins: 900,
  /** Overshoot settle when a reel lands. */
  settleMs: 190,
  /** Scroll speed, px/s. */
  scrollSpeed: 2600,
}

export const DEFAULT_LINES_TIMING = {
  /** One winning line lit on its own. */
  lineShowMs: 700,
  /** Gap between consecutive lines in the cycle. */
  betweenLinesMs: 120,
  /** Every winning line shown together, once the cycle is done. */
  allLinesMs: 1000,
}

const NO_PLAQUE = { image: null, cap: 0, fieldTop: 0, fieldBottom: 0, shadow: null }

export const DEFAULT_BANNERS: Record<BannerKind, ResolvedBanner> = {
  free: { fill: 0xe0457b, text: 0xfff0f5, ...NO_PLAQUE },
  win: { fill: 0x4ade80, text: 0x052e16, ...NO_PLAQUE },
  bomb: { fill: 0xfbbf24, text: 0x111827, ...NO_PLAQUE },
}

export const DEFAULT_BOMB: SymbolStyle = {
  fill: 0x111827,
  text: 0xfbbf24,
  label: '×',
  fontSize: 30,
  name: 'Multiplier bomb',
}

const DEFAULT_FONT = {
  family: 'Inter, Segoe UI, system-ui, sans-serif',
  weight: '800',
  letterSpacing: 0.5,
}

/** Overlay a theme's partial style on top of a fully-formed fallback. */
function merge(base: SymbolStyle, over: ThemeSymbolStyle | undefined): SymbolStyle {
  if (!over) return base
  return {
    fill: over.fill ?? base.fill,
    text: over.text ?? base.text,
    label: over.label ?? base.label,
    fontSize: over.fontSize ?? base.fontSize,
    name: base.name,
    frame: over.frame ?? base.frame,
    image: over.image ?? base.image,
    backgroundImage: over.backgroundImage ?? base.backgroundImage,
    scale: over.scale ?? base.scale,
    animation: over.animation
      ? { images: over.animation.images, fps: over.animation.fps ?? 8 }
      : base.animation,
  }
}

/** Theme-relative path → URL the browser can fetch. */
function url(baseUrl: string, path: string | undefined): string | null {
  return path ? baseUrl + path : null
}

/**
 * Merge one banner over its default. `image` cannot ride along with the
 * rest of the spread: the manifest holds a path relative to the theme and
 * the renderer needs a URL.
 */
function banner(
  base: ResolvedBanner,
  raw: NonNullable<Theme['banners']>['free'],
  baseUrl: string,
): ResolvedBanner {
  return { ...base, ...strip(raw), image: url(baseUrl, raw?.image) ?? base.image }
}

/**
 * Resolve a theme for one game.
 *
 * `theme` may be null — that is the no-theme path, and it must stay a
 * first-class case rather than an error branch: games appear in the lobby
 * the moment their math is released, long before anyone draws them.
 */
export function resolveTheme(
  theme: Theme | null,
  symbols: readonly GameSymbol[],
  baseUrl = '',
): ResolvedTheme {
  const styles: Record<string, SymbolStyle> = {}
  for (const sym of symbols) {
    const style = merge(generatedStyle(sym), theme?.symbols?.[sym.code])
    // Image paths become absolute here, once, so nothing downstream has to
    // remember which directory a theme came from.
    if (style.image) style.image = baseUrl + style.image
    if (style.backgroundImage) style.backgroundImage = baseUrl + style.backgroundImage
    if (style.animation) {
      style.animation = {
        ...style.animation,
        images: style.animation.images.map((p) => baseUrl + p),
      }
    }
    styles[sym.code] = style
  }

  return {
    id: theme?.id ?? 'fallback',
    name: theme?.name ?? 'Placeholder',
    header: {
      logoImage: url(baseUrl, theme?.header?.logoImage),
    },
    winTitles: {
      big: url(baseUrl, theme?.winTitles?.big),
      mega: url(baseUrl, theme?.winTitles?.mega),
      epic: url(baseUrl, theme?.winTitles?.epic),
    },
    winCrystals: {
      red: url(baseUrl, theme?.winCrystals?.red),
      blue: url(baseUrl, theme?.winCrystals?.blue),
    },
    winButton: url(baseUrl, theme?.winButton),
    symbols: styles,
    bomb: (() => {
      const bomb = merge(DEFAULT_BOMB, theme?.bomb)
      if (bomb.image) bomb.image = baseUrl + bomb.image
      if (bomb.backgroundImage) bomb.backgroundImage = baseUrl + bomb.backgroundImage
      return bomb
    })(),
    board: {
      background: theme?.board?.background ?? null,
      backgroundImage: url(baseUrl, theme?.board?.backgroundImage),
      frameImage: url(baseUrl, theme?.board?.frameImage),
      frameInset: theme?.board?.frameInset ?? null,
      frameOpeningInset: theme?.board?.frameOpeningInset ?? null,
      tileRadius: theme?.board?.tileRadius ?? 16,
      gap: theme?.board?.gap ?? null,
      cellSize: theme?.board?.cellSize ?? null,
      gridPadding: theme?.board?.gridPadding ?? 0,
      symbolPadding: theme?.board?.symbolPadding ?? 0,
      symbolFit: theme?.board?.symbolFit ?? 'contain',
    },
    stage: {
      background: theme?.stage?.background ?? null,
      backgroundImage: url(baseUrl, theme?.stage?.backgroundImage),
      mascotImage: url(baseUrl, theme?.stage?.mascotImage),
      decorImage: url(baseUrl, theme?.stage?.decorImage),
      ghostImage: url(baseUrl, theme?.stage?.ghostImage),
      // The stock panel stays unless the theme brings its own frame art:
      // two cabinets around one board is the most common first mistake.
      panel: theme?.stage?.panel ?? !theme?.board?.frameImage,
      panelFrame: theme?.stage?.panelFrame
        ? {
            border: theme.stage.panelFrame.border,
            background: theme.stage.panelFrame.background ?? null,
          }
        : null,
      celebration: theme?.stage?.celebration ?? 'flames',
      cloudImages: (theme?.stage?.cloudImages ?? [])
        .map((p) => url(baseUrl, p))
        .filter((u): u is string => u !== null),
      emitters: (theme?.stage?.emitters ?? []).map((e) => ({
        kind: e.kind,
        x: e.x,
        y: e.y,
        width: e.width,
        height: e.height,
        curve: e.curve ?? 0,
        span: e.span ?? 1,
        // A pale blue-white: real falling water is white foam with a hint
        // of the sky in it, not the saturated blue of a paint bucket.
        color: e.color ?? 0xdff2ff,
        rate: e.rate ?? 60,
        alpha: e.alpha ?? 1,
        // Slow by default: a gem that breathes faster than a resting human
        // reads as a warning light, not as magic.
        period: e.period ?? 4,
      })),
    },
    audio: {
      music: url(baseUrl, theme?.audio?.music),
      musicFree: url(baseUrl, theme?.audio?.musicFree),
      // Quiet by default: the loop is meant to sit under the reels, and a
      // theme that wants more says so explicitly.
      musicVolume: theme?.audio?.musicVolume ?? 0.35,
      sfx: Object.fromEntries(
        Object.entries(theme?.audio?.sfx ?? {}).map(([name, path]) => [name, baseUrl + path]),
      ),
      sfxVolume: theme?.audio?.sfxVolume ?? 0.8,
    },
    font: {
      family: theme?.font?.family ?? DEFAULT_FONT.family,
      weight: theme?.font?.weight ?? DEFAULT_FONT.weight,
      letterSpacing: theme?.font?.letterSpacing ?? DEFAULT_FONT.letterSpacing,
    },
    banners: {
      free: banner(DEFAULT_BANNERS.free, theme?.banners?.free, baseUrl),
      win: banner(DEFAULT_BANNERS.win, theme?.banners?.win, baseUrl),
      bomb: banner(DEFAULT_BANNERS.bomb, theme?.banners?.bomb, baseUrl),
    },
    timings: {
      tumble: { ...DEFAULT_TUMBLE_TIMING, ...strip(theme?.timings?.tumble) },
      ways: { ...DEFAULT_WAYS_TIMING, ...strip(theme?.timings?.ways) },
      lines: { ...DEFAULT_LINES_TIMING, ...strip(theme?.timings?.lines) },
    },
    atlas: theme?.atlas
      ? { url: baseUrl + theme.atlas.url, backgroundFrame: theme.atlas.backgroundFrame ?? null }
      : null,
    textures: new Map(),
  }
}

/**
 * Drop undefined keys before spreading.
 *
 * Zod leaves absent optionals as `undefined` keys, and `{...defaults,
 * ...{dropMs: undefined}}` would overwrite the default with undefined —
 * a silent NaN in the tween loop rather than a visible error.
 */
function strip<T extends object>(obj: T | undefined): Partial<T> {
  if (!obj) return {}
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v
  }
  return out as Partial<T>
}

/** The theme every game gets before anyone draws it. */
export function fallbackTheme(symbols: readonly GameSymbol[]): ResolvedTheme {
  return resolveTheme(null, symbols)
}
